import Alert from "@mui/joy/Alert";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Stack from "@mui/joy/Stack";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState } from "react";
import AppCTA from "@components/AppCTA/AppCTA";
import GeneralConst from "@consts/GeneralConst";
import UserService from "@services/UserService";
import AlertStrings from "@strings/AlertStrings.json";
import GeneralStrings from "@strings/GeneralStrings.json";
import { validateEmail } from "@utilities/ValidationHelper";
import LoginLayout from "@pages/LoginPage/LoginLayout";

interface FormValues {
  email: string;
}

interface ResponseData {
  status: string;
  statusMessage?: string;
}

const ForgotPasswordForm = ({
  setFormType,
}: {
  setFormType: (formType: string) => void;
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const STATUS_CODES = GeneralConst.STATUS_CODES;

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ): void => {
    event.preventDefault();
    setFormType(GeneralConst.LOGIN_FORM_TYPE.LOGIN);
  };

  const resetErrorMessages = () => {
    setErrorMessage(null);
    setFormStatus(null);
  };

  const handleSubmit = async (formValues: FormValues): Promise<void> => {
    resetErrorMessages();

    try {
      const response: { data: ResponseData } =
        await UserService.generateTemporaryPassword(formValues.email);
      if (response.data.status === STATUS_CODES.ERROR) {
        setErrorMessage(
          response?.data?.statusMessage || AlertStrings.generalError
        );
        setFormStatus(STATUS_CODES.ERROR);
      } else {
        setSuccessMessage(
          response?.data?.statusMessage || AlertStrings.generalSuccess
        );
        setFormStatus(STATUS_CODES.SUCCESS);
      }
    } catch (response: unknown) {
      const errorResponse = response as { message?: string };
      setErrorMessage(errorResponse?.message || AlertStrings.generalError);
      setFormStatus(STATUS_CODES.ERROR);
    }
  };

  return (
    <LoginLayout>
      <Stack gap={4} sx={{ mb: 2 }}>
        <Stack gap={1}>
          <h3>{GeneralStrings.form.forgotPassword}</h3>
        </Stack>
      </Stack>

      {formStatus === STATUS_CODES.ERROR && (
        <Alert
          startDecorator={<i className="fa-solid fa-circle-exclamation" />}
          variant="solid"
          color="danger"
          size="lg"
        >
          {errorMessage}
        </Alert>
      )}

      {formStatus === STATUS_CODES.SUCCESS && (
        <Alert
          startDecorator={<i className="fa-solid fa-circle-check" />}
          variant="solid"
          color="success"
          size="lg"
        >
          {successMessage}
        </Alert>
      )}

      <Stack gap={4} sx={{ mt: 2 }}>
        <Formik
          initialValues={{
            email: "",
          }}
          onSubmit={handleSubmit}
        >
          {({ submitForm }) => (
            <Form>
              <FormControl required>
                <FormLabel>{GeneralStrings.form.email}</FormLabel>
                <Field
                  name="email"
                  className="form-control"
                  validate={validateEmail}
                  type="email"
                  data-testid="email-input"
                />
                <ErrorMessage
                  component="span"
                  className="form-error"
                  name="email"
                />
              </FormControl>
              <Stack gap={4} sx={{ mt: 2 }}>
                <Button
                  className="btn-link"
                  onClick={(event) => handleClick(event)}
                >
                  <i className="fa-solid fa-angle-left go-back-arrow" />
                  {GeneralStrings.form.goBack}
                </Button>
                <AppCTA
                  type="button"
                  variant="primary"
                  fullWidth
                  label={GeneralStrings.form.sendTemporaryPassword}
                  onClick={async () => {
                    await submitForm();
                  }}
                />
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </LoginLayout>
  );
};

export default ForgotPasswordForm;
